import type { Traducao } from '../tipos';

/**
 * Tabelas, checklists e assinatura do editor da apreciação.
 *
 * Três blocos vivem aqui por serem lista fixa em português que a tela mostra
 * mas o banco guarda: `classificacao` (grau de risco), `hrn`/`srpcs`
 * (parâmetros das normas), `partesCorpo` e `perigos.texto`. Em todos eles o
 * português continua sendo o valor gravado — o dicionário só diz como aquele
 * valor aparece na tela. Ver `components/apreciacao/constants/`.
 */
export const apreciacaoTabelas: Traducao = {
  pt: {
    partes: {
      titulo: 'Tabela Geral das Partes',
      vazio: 'Nenhuma parte cadastrada. Clique em "Adicionar Parte" para começar.',
      semParteDefinida: 'Sem parte definida',
      coluna: {
        parte: 'Parte',
        hrnMax: 'HRN Máx',
        classificacao: 'Classificação',
        conformidade: 'Conformidade',
      },
      aria: {
        reordenar: 'Reordenar',
      },
      status: {
        pronta: 'Pronta',
        naoPronta: 'Não Pronta',
      },
      conformidade: {
        conforme: 'Conforme',
        naoConforme: 'Não Conforme',
      },
      acao: {
        adicionar: 'Adicionar Parte',
        adicionarTemplate: 'Adicionar de Template',
        adicionarDica: 'Clique normal: adicionar manualmente. Ctrl/Cmd + clique: importar JSON',
        arrastar: 'Arrastar para reordenar',
        editar: 'Editar Parte',
        detalharIA: 'Detalhar com IA',
        baixarImagens: 'Baixar imagens desta parte (ZIP)',
        criarTemplate: 'Criar Template desta Parte',
        duplicar: 'Duplicar Parte',
        deletar: 'Deletar Parte',
      },
      confirmarDeletar: {
        um: 'Tem certeza que deseja deletar a parte "{{parte}}"?\n\nEsta ação irá remover {{qtd}} risco e não pode ser desfeita.',
        varios: 'Tem certeza que deseja deletar a parte "{{parte}}"?\n\nEsta ação irá remover {{qtd}} riscos e não pode ser desfeita.',
      },
      resumo: {
        totalPartes: 'Total de Partes:',
        prontas: 'Prontas:',
        naoProntas: 'Não Prontas:',
        conformes: 'Conformes:',
        totalRiscos: 'Total de Riscos:',
      },
    },
    riscos: {
      titulo: 'Tabela Geral de Riscos',
      vazio: 'Nenhum risco cadastrado. Clique em "Adicionar Risco" para começar.',
      coluna: {
        descricao: 'Descrição do Risco',
        classificacao: 'Classificação',
        conformidade: 'Conformidade',
      },
      conformidade: {
        conforme: 'Conforme',
        naoConforme: 'Não Conforme',
      },
      acao: {
        adicionar: 'Adicionar Risco',
      },
      resumo: {
        totalRiscos: 'Total de Riscos:',
        criticos: 'Críticos:',
        altos: 'Altos:',
        mediosBaixos: 'Médios/Baixos:',
      },
    },
    classificacao: {
      inaceitavel: 'Inaceitável',
      extremo: 'Extremo',
      muitoAlto: 'Muito alto',
      alto: 'Alto',
      significante: 'Significante',
      baixo: 'Baixo',
      muitoBaixo: 'Muito baixo',
      aceitavel: 'Aceitável',
      critico: 'Crítico',
      medio: 'Médio',
      baixoSignificativo: 'Baixo, porém significativo',
    },
    checklist: {
      carregando: 'Carregando comentários do cliente…',
      titulo: 'Revisão {{rodada}} — comentários do cliente',
      devolvidoNa: 'devolvido na {{guia}}',
      pdfComentado: 'PDF comentado',
      abrirRevisao: 'Abrir revisão',
      vazio: 'Nenhum comentário para esta parte.',
      semParte: 'Sem parte identificada',
      aFazer: '{{qtd}} a fazer',
      tudoTratado: 'tudo tratado',
      itemCabecalho: '#{{indice}} · p. {{pagina}} · {{tipo}}',
      parteDeduzida: '· parte deduzida',
      parteDeduzidaDica: 'Parte deduzida da página do comentário — confirme na tela da revisão se estiver errada.',
      oQueFoiFeito: 'O que foi feito:',
      placeholderResolucao: 'O que foi feito nesta parte (ou por que não se aplica)…',
      status: {
        feito: 'Feito',
        fazendo: 'Fazendo',
        naoSeAplica: 'Não se aplica',
      },
      statusItem: {
        pendente: 'Pendente',
        em_andamento: 'Fazendo',
        feito: 'Feito',
        nao_aplicavel: 'Não aplicável',
      },
      tipoAnotacao: {
        Text: 'Balão',
        Highlight: 'Destaque',
        StrikeOut: 'Tachado',
        Underline: 'Sublinhado',
        Squiggly: 'Ondulado',
        FreeText: 'Caixa de texto',
        Square: 'Retângulo',
        Circle: 'Elipse',
        Manual: 'Digitado',
      },
      acao: {
        editarResolucao: 'Editar o que foi feito',
        anotarResolucao: 'Anotar o que foi feito',
      },
      erro: {
        carregar: 'Não foi possível carregar o checklist.',
        carregarRevisao: 'Não foi possível carregar o checklist de revisão.',
        salvar: 'Não foi possível salvar.',
        salvarAlteracao: 'Não foi possível salvar a alteração.',
      },
    },
    administrativa: {
      titulo: 'Lista de Verificação Administrativa da Máquina',
      placeholderObservacao: 'Observações...',
      coluna: {
        item: 'Item',
        possui: 'Possui?',
        observacao: 'Observação',
      },
      possui: {
        naoInformado: 'N/I',
      },
      acao: {
        possui: 'Possui',
        naoPossui: 'Não possui',
        naoInformado: 'Não informado',
      },
      itens: {
        C_4_1: 'Estudo de classificação de áreas IEC 60079',
        C_4_2: 'Manual de instruções de operação',
        C_4_3: 'Plano de manutenção preventiva ou corretiva',
        C_4_4: 'Plano de bloqueio de energias',
        C_4_5: 'Registro de treinamento para colaboradores',
        C_4_6: 'Diagrama elétrico',
        C_4_7: 'Diagrama hidráulico',
        C_4_8: 'Diagrama pneumático',
        C_4_9: 'LTCAT - Laudo Técnico das Condições Ambientais do Trabalho',
        C_4_10: 'AET - Análise Ergonômica do Trabalho',
      },
    },
    assinatura: {
      rotuloPadrao: 'Assinatura',
      alt: 'Assinatura',
      vazio: 'Nenhuma assinatura',
      acao: {
        upload: 'Upload de Imagem',
        digital: 'Captura Digital',
        concluir: 'Concluir',
        remover: 'Remover assinatura',
        alterarUpload: 'Alterar (Upload)',
        alterarDigital: 'Alterar (Digital)',
      },
    },
    navegacao: {
      dadosMaquina: 'Dados da Máquina',
      partesMaquina: 'Partes da Máquina',
    },
    analise: {
      titulo: 'Análise — Lista de Verificação Administrativa',
      selecionadas: '{{qtd}} apreciação(ões) selecionada(s)',
      progresso: ' — carregando {{feitos}}/{{total}}…',
      carregando: 'Carregando dados...',
      vazio: 'Nenhum dado disponível.',
      totais: 'Totais',
      totalPossui: 'Possui: {{qtd}}',
      totalNao: 'Não: {{qtd}}',
      totalSemInfo: 's/info: {{qtd}}',
      coluna: {
        maquina: 'Máquina',
        setor: 'Setor',
        apreciacao: 'Apreciação',
      },
      rotuloPossui: {
        sim: 'Possui',
        nao: 'Não Possui',
        semInfo: '—',
      },
      acao: {
        exportar: 'Exportar Excel',
      },
      excel: {
        aba: 'Verificação Adm.',
        apreciacao: 'Apreciação',
        maquina: 'Máquina',
        setor: 'Setor',
        identificacao: 'Identificação',
        obs: 'Obs.',
        totais: 'TOTAIS',
        resumoItem: 'Possui: {{possui}} | Não: {{nao}} | s/info: {{semInfo}}',
      },
      toast: {
        gerada: 'Planilha gerada',
        erro: 'Erro ao gerar Excel',
      },
    },
    hrn: {
      gs: {
        arranhaoContusao: 'Arranhão / contusão',
        laceracaoCorte: 'Laceração / corte / leve problema de saúde',
        ossosPequenos: 'Quebra - ossos pequenos – dedos',
        ossosGrandes: 'Quebra - ossos grandes – mão / braços / pernas',
        perdaDedos: 'Perda de 1 ou 2 dedos',
        amputacaoParcial: 'Amputação de perna / braço, perda parcial da visão audição',
        amputacaoTotal: 'Amputação de 2 pernas / braços, perda total da visão / audição em ambos os olhos / ouvidos',
        doencaGrave: 'Doença grave ou permanente',
        fatalidade: 'Fatalidade',
      },
      fe: {
        anualmente: 'Anualmente',
        mensalmente: 'Mensalmente',
        semanalmente: 'Semanalmente',
        diariamente: 'Diariamente',
        porHora: 'Em termos de hora',
        constantemente: 'Constantemente',
      },
      po: {
        quaseImpossivel: 'Quase impossível',
        muitoImprovavel: 'Muito improvável',
        improvavel: 'Improvável',
        possivel: 'Possível',
        plausivel: 'Plausível',
        provavel: 'Provável',
        muitoProvavel: 'Muito provável',
        quaseCerteza: 'Quase certeza',
      },
      np: {
        de1a2: '1 a 2 pessoas',
        de3a7: '3 a 7 pessoas',
        de8a15: '8 a 15 pessoas',
        de16a50: '16 a 50 pessoas',
        maisDe50: 'Mais que 50 pessoas',
      },
    },
    srpcs: {
      severidade: {
        S1: 'Lesão Leve (normalmente reversível)',
        S2: 'Lesão Grave (normalmente irreversível) incluindo morte',
      },
      frequencia: {
        F1: 'Raro a relativamente frequente e/ou baixo tempo de exposição',
        F2: 'Frequente a contínuo e/ou tempo de exposição longo',
      },
      possibilidade: {
        P1: 'Possível sob condições específicas',
        P2: 'Quase nunca possível',
      },
      categoria: {
        B: 'Categoria B - Requisitos básicos de segurança',
        '1': 'Categoria 1 - Requisitos básicos de segurança e componentes bem testados',
        '2': 'Categoria 2 - Monitoramento periódico da função de segurança',
        '3': 'Categoria 3 - Exigência de redundância e monitoramento',
        '4': 'Categoria 4 - Exigência de redundância e monitoramento contínuo',
      },
      plr: {
        a: 'PLr a - Baixo',
        b: 'PLr b - Baixo a médio',
        c: 'PLr c - Médio',
        d: 'PLr d - Médio a alto',
        e: 'PLr e - Alto',
      },
      sil: {
        '1': 'SIL 1 - Baixo',
        '2': 'SIL 2 - Médio',
        '3': 'SIL 3 - Alto',
        '4': 'SIL 4 - Muito alto',
      },
      observacao: {
        avaliarCasoEspecifico: 'PL c (ou d) - avaliar caso específico',
      },
    },
    partesCorpo: {
      corpoInteiro: 'Corpo inteiro',
      cabeca: 'Cabeça',
      pescoco: 'Pescoço',
      torax: 'Tórax',
      membrosSuperiores: 'Membros superiores',
      membrosInferiores: 'Membros inferiores',
      maos: 'Mãos',
      pes: 'Pés',
      olhos: 'Olhos',
      face: 'Face',
    },
    perigos: {
      texto: {
      aceleracaoDesaceleracao: 'aceleração, desaceleração',
      cantosVivos: 'cantos vivos',
      aproximacaoDeUmElementoMovelAUmaParteFixa: 'aproximação de um elemento móvel a uma parte fixa',
      corteDePecas: 'corte de peças',
      elementosElasticos: 'elementos elásticos',
      quedaDeObjetos: 'queda de objetos',
      gravidade: 'gravidade',
      alturaAPartirDoSolo: 'altura a partir do solo',
      altaPressao: 'alta pressão',
      instabilidade: 'instabilidade',
      energiaCinetica: 'energia cinética',
      mobilidadeDaMaquina: 'mobilidade da máquina',
      elementosMoveis: 'elementos móveis',
      elementosRotativos: 'elementos rotativos',
      superficieAsperaEscorregadia: 'superfície áspera, escorregadia',
      arestasCortantes: 'arestas cortantes',
      energiaArmazenada: 'energia armazenada',
      vacuo: 'vácuo',
      atropelamentos: 'atropelamentos',
      arremessos: 'arremessos',
      esmagamento: 'esmagamento',
      corteEMutilacao: 'corte e mutilação',
      segurarEPrender: 'segurar e prender',
      enroscar: 'enroscar',
      friccaoEAbrasao: 'fricção e abrasão',
      impacto: 'impacto',
      injecao: 'injeção',
      raspagem: 'raspagem',
      escorregamentoTropecoEQueda: 'escorregamento, tropeço e queda',
      perfuracao: 'perfuração',
      sufocamento: 'sufocamento',
      arcos: 'arcos',
      fenomenosEletromagneticos: 'fenômenos eletromagnéticos',
      partesVivas: 'partes vivas',
      baixaRigidezDieletrica: 'baixa rigidez dielétrica',
      partesVivasSobCondicoesDeFalha: 'partes vivas sob condições de falha',
      curtoCircuito: 'curto-circuito',
      radiacaoTermica: 'radiação térmica',
      queimadura: 'queimadura',
      efeitosQuimicos: 'efeitos químicos',
      efeitosEmImplantesMedicos: 'efeitos em implantes médicos',
      eletrocussao: 'eletrocussão',
      quedaOuArremesso: 'queda ou arremesso',
      incendio: 'incêndio',
      projecaoDeFagulhas: 'projeção de fagulhas',
      choque: 'choque',
      explosao: 'explosão',
      objetosOuMateriaisComAltaOuBaixaTemperatura: 'objetos ou materiais com alta ou baixa temperatura',
      radiacaoProvenienteDeFontesQuentes: 'radiação proveniente de fontes quentes',
      desidratacao: 'desidratação',
      desconforto: 'desconforto',
      congelamento: 'congelamento',
      danosCausadosPelaRadiacaoDeFontesQuentes: 'danos causados pela radiação de fontes quentes',
      escaldo: 'escaldo',
      fenomenoDeCavitacao: 'fenômeno de cavitação',
      sistemaDeExaustao: 'sistema de exaustão',
      vazamentoDeGasEmAltaVelocidade: 'vazamento de gás em alta velocidade',
      processosDeProducaoEstampagemCorteEtc: 'processos de produção (estampagem, corte, etc.)',
      partesMoveis: 'partes móveis',
      superficiesEmAtrito: 'superfícies em atrito',
      pecasRotativasDesbalanceadas: 'peças rotativas desbalanceadas',
      ruidosPneumaticos: 'ruídos pneumáticos',
      pecasDesgastadas: 'peças desgastadas',
      perdaDeConsciencia: 'perda de consciência',
      perdaDeEquilibrio: 'perda de equilíbrio',
      perdaPermanenteDeAudicao: 'perda permanente de audição',
      estresse: 'estresse',
      zumbido: 'zumbido',
      cansaco: 'cansaço',
      outrosComoPorExemploMecanicosEletricosEmDecorrenciaDaInterferenciaEmComunicacoesOuSinaisAcusticos: 'outros como (por exemplo mecânicos, elétricos) em decorrência da interferência em comunicações ou sinais acústicos',
      desalinhamentoDePartesMoveis: 'desalinhamento de partes móveis',
      equipamentosMoveis: 'equipamentos móveis',
      equipamentosQueVibram: 'equipamentos que vibram',
      morbidadeLombar: 'morbidade lombar',
      disfuncoesNeurologicas: 'disfunções neurológicas',
      disfuncoesOsteoarticulares: 'disfunções osteoarticulares',
      traumasNaColuna: 'traumas na coluna',
      disfuncoesVasculares: 'disfunções vasculares',
      fontesDeRadiacaoIonizante: 'fontes de radiação ionizante',
      radiacoesEletromagneticasDeBaixaFrequencia: 'radiações eletromagnéticas de baixa frequência',
      radiacaoOticaInfravermelhoVisivelOuUltravioletaIncluindoLaser: 'radiação ótica (infravermelho, visível ou ultravioleta), incluindo laser',
      radiacoesEletromagneticasEmRadiofrequencia: 'radiações eletromagnéticas em radiofrequência',
      danosNosOlhosEPele: 'danos nos olhos e pele',
      efeitosNaCapacidadeReprodutiva: 'efeitos na capacidade reprodutiva',
      mutacoes: 'mutações',
      dorDeCabecaInsoniaEtc: 'dor de cabeça, insônia, etc',
      efeitosCancerigenos: 'efeitos cancerígenos',
      aerossois: 'aerossóis',
      agentesBiologicosEMicrobiologicosViraisOuBacterianos: 'agentes biológicos e microbiológicos (virais ou bacterianos)',
      combustiveis: 'combustíveis',
      poeira: 'poeira',
      explosivos: 'explosivos',
      fibras: 'fibras',
      inflamaveis: 'inflamáveis',
      fluidos: 'fluídos',
      fumos: 'fumos',
      gases: 'gases',
      misturas: 'misturas',
      oxidantes: 'oxidantes',
      dificuldadesParaRespiracaoAsfixia: 'dificuldades para respiração, asfixia',
      cancer: 'câncer',
      corrosao: 'corrosão',
      explosoes: 'explosões',
      fogo: 'fogo',
      infeccoes: 'infecções',
      envenenamento: 'envenenamento',
      sensibilizacao: 'sensibilização',
      acesso: 'acesso',
      projetoOuLocalizacaoDeIndicadoresIndicadoresEPaineis: 'projeto ou localização de indicadores, indicadores e painéis',
      projetoLocalizacaoOuIdentificacaoDeDispositivosDeComando: 'projeto, localização ou identificação de dispositivos de comando',
      esforcos: 'esforços',
      cintilacaoBrilhoSombraEfeitoEstroboscopio: 'cintilação, brilho, sombra, efeito estroboscópio',
      luzLocal: 'luz local',
      sobrecargaMentalOciosidade: 'sobrecarga mental, ociosidade',
      postura: 'postura',
      atividadeRepetitiva: 'atividade repetitiva',
      visibilidade: 'visibilidade',
      fadiga: 'fadiga',
      disturbiosMusculoEsqueleticos: 'distúrbios músculo esqueléticos',
      outrosComoPorExemplo: 'outros como (por exemplo...)',
      poeiraOuNeblina: 'poeira ou neblina',
      pertubacaoEletromagnetica: 'pertubação eletromagnética',
      descargasAtmosfericas: 'descargas atmosféricas',
      umidade: 'umidade',
      poluicao: 'poluição',
      neve: 'neve',
      temperatura: 'temperatura',
      agua: 'água',
      vento: 'vento',
      faltaDeOxigenio: 'falta de oxigênio',
      doencasBrandas: 'doenças brandas',
      quedaOuEscorregamento: 'queda ou escorregamento',
      asfixia: 'asfixia',
      qualquerOutraConsequenciaDoEfeitoCausadoPorFontesDePerigosDaMaquinaOuPartesDaMesma: 'qualquer outra consequência do efeito causado por fontes de perigos da máquina ou partes da mesma',
      porExemploAtividadesRepetitivasEsforcosAmbientesEmAltaTemperatura: 'por exemplo, atividades repetitivas + esforços + ambientes em alta temperatura',
      porExemploDesidratacaoPerdaDeConscienciaEAtaqueCardiaco: 'por exemplo, desidratação, perda de consciência e ataque cardíaco',
      },
    },
  },
  en: {
    partes: {
      titulo: 'General Parts Table',
      vazio: 'No parts registered yet. Click "Add part" to get started.',
      semParteDefinida: 'No part defined',
      coluna: {
        parte: 'Part',
        hrnMax: 'Max HRN',
        classificacao: 'Classification',
        conformidade: 'Compliance',
      },
      aria: {
        reordenar: 'Reorder',
      },
      status: {
        pronta: 'Ready',
        naoPronta: 'Not ready',
      },
      conformidade: {
        conforme: 'Compliant',
        naoConforme: 'Non-compliant',
      },
      acao: {
        adicionar: 'Add part',
        adicionarTemplate: 'Add from template',
        adicionarDica: 'Normal click: add manually. Ctrl/Cmd + click: import JSON',
        arrastar: 'Drag to reorder',
        editar: 'Edit part',
        detalharIA: 'Detail with AI',
        baixarImagens: 'Download images of this part (ZIP)',
        criarTemplate: 'Create template from this part',
        duplicar: 'Duplicate part',
        deletar: 'Delete part',
      },
      confirmarDeletar: {
        um: 'Are you sure you want to delete the part "{{parte}}"?\n\nThis will remove {{qtd}} hazard and cannot be undone.',
        varios: 'Are you sure you want to delete the part "{{parte}}"?\n\nThis will remove {{qtd}} hazards and cannot be undone.',
      },
      resumo: {
        totalPartes: 'Total parts:',
        prontas: 'Ready:',
        naoProntas: 'Not ready:',
        conformes: 'Compliant:',
        totalRiscos: 'Total risks:',
      },
    },
    riscos: {
      titulo: 'General Risk Table',
      vazio: 'No risks registered yet. Click "Add risk" to get started.',
      coluna: {
        descricao: 'Risk description',
        classificacao: 'Classification',
        conformidade: 'Compliance',
      },
      conformidade: {
        conforme: 'Compliant',
        naoConforme: 'Non-compliant',
      },
      acao: {
        adicionar: 'Add risk',
      },
      resumo: {
        totalRiscos: 'Total risks:',
        criticos: 'Critical:',
        altos: 'High:',
        mediosBaixos: 'Medium/Low:',
      },
    },
    classificacao: {
      inaceitavel: 'Unacceptable',
      extremo: 'Extreme',
      muitoAlto: 'Very high',
      alto: 'High',
      significante: 'Significant',
      baixo: 'Low',
      muitoBaixo: 'Very low',
      aceitavel: 'Acceptable',
      critico: 'Critical',
      medio: 'Medium',
      baixoSignificativo: 'Low, but significant',
    },
    checklist: {
      carregando: 'Loading client comments…',
      titulo: 'Review {{rodada}} — client comments',
      devolvidoNa: 'returned on {{guia}}',
      pdfComentado: 'Commented PDF',
      abrirRevisao: 'Open review',
      vazio: 'No comments for this part.',
      semParte: 'No part identified',
      aFazer: '{{qtd}} to do',
      tudoTratado: 'all handled',
      itemCabecalho: '#{{indice}} · p. {{pagina}} · {{tipo}}',
      parteDeduzida: '· inferred part',
      parteDeduzidaDica: 'Part inferred from the comment page — confirm it on the review screen if it is wrong.',
      oQueFoiFeito: 'What was done:',
      placeholderResolucao: 'What was done in this part (or why it does not apply)…',
      status: {
        feito: 'Done',
        fazendo: 'Doing',
        naoSeAplica: 'Not applicable',
      },
      statusItem: {
        pendente: 'Pending',
        em_andamento: 'Doing',
        feito: 'Done',
        nao_aplicavel: 'Not applicable',
      },
      tipoAnotacao: {
        Text: 'Sticky note',
        Highlight: 'Highlight',
        StrikeOut: 'Strikethrough',
        Underline: 'Underline',
        Squiggly: 'Squiggly',
        FreeText: 'Text box',
        Square: 'Rectangle',
        Circle: 'Ellipse',
        Manual: 'Typed',
      },
      acao: {
        editarResolucao: 'Edit what was done',
        anotarResolucao: 'Note what was done',
      },
      erro: {
        carregar: 'Could not load the checklist.',
        carregarRevisao: 'Could not load the review checklist.',
        salvar: 'Could not save.',
        salvarAlteracao: 'Could not save the change.',
      },
    },
    administrativa: {
      titulo: 'Machine Administrative Checklist',
      placeholderObservacao: 'Notes...',
      coluna: {
        item: 'Item',
        possui: 'Available?',
        observacao: 'Note',
      },
      possui: {
        naoInformado: 'N/S',
      },
      acao: {
        possui: 'Available',
        naoPossui: 'Not available',
        naoInformado: 'Not stated',
      },
      itens: {
        C_4_1: 'IEC 60079 area classification study',
        C_4_2: 'Operating instructions manual',
        C_4_3: 'Preventive or corrective maintenance plan',
        C_4_4: 'Energy lockout plan',
        C_4_5: 'Employee training records',
        C_4_6: 'Electrical diagram',
        C_4_7: 'Hydraulic diagram',
        C_4_8: 'Pneumatic diagram',
        C_4_9: 'LTCAT - Technical Report on Environmental Working Conditions',
        C_4_10: 'AET - Ergonomic Work Analysis',
      },
    },
    assinatura: {
      rotuloPadrao: 'Signature',
      alt: 'Signature',
      vazio: 'No signature',
      acao: {
        upload: 'Upload image',
        digital: 'Digital capture',
        concluir: 'Done',
        remover: 'Remove signature',
        alterarUpload: 'Change (Upload)',
        alterarDigital: 'Change (Digital)',
      },
    },
    navegacao: {
      dadosMaquina: 'Machine Data',
      partesMaquina: 'Machine Parts',
    },
    analise: {
      titulo: 'Analysis — Administrative Checklist',
      selecionadas: '{{qtd}} assessment(s) selected',
      progresso: ' — loading {{feitos}}/{{total}}…',
      carregando: 'Loading data...',
      vazio: 'No data available.',
      totais: 'Totals',
      totalPossui: 'Available: {{qtd}}',
      totalNao: 'No: {{qtd}}',
      totalSemInfo: 'n/s: {{qtd}}',
      coluna: {
        maquina: 'Machine',
        setor: 'Sector',
        apreciacao: 'Assessment',
      },
      rotuloPossui: {
        sim: 'Available',
        nao: 'Not available',
        semInfo: '—',
      },
      acao: {
        exportar: 'Export to Excel',
      },
      excel: {
        aba: 'Admin. checklist',
        apreciacao: 'Assessment',
        maquina: 'Machine',
        setor: 'Sector',
        identificacao: 'Identification',
        obs: 'Notes',
        totais: 'TOTALS',
        resumoItem: 'Available: {{possui}} | No: {{nao}} | n/s: {{semInfo}}',
      },
      toast: {
        gerada: 'Spreadsheet generated',
        erro: 'Could not generate the Excel file',
      },
    },
    hrn: {
      gs: {
        arranhaoContusao: 'Scratch / bruise',
        laceracaoCorte: 'Laceration / cut / minor health issue',
        ossosPequenos: 'Break - small bones – fingers',
        ossosGrandes: 'Break - large bones – hand / arms / legs',
        perdaDedos: 'Loss of 1 or 2 fingers',
        amputacaoParcial: 'Amputation of leg / arm, partial loss of sight or hearing',
        amputacaoTotal: 'Amputation of 2 legs / arms, total loss of sight / hearing in both eyes / ears',
        doencaGrave: 'Serious or permanent illness',
        fatalidade: 'Fatality',
      },
      fe: {
        anualmente: 'Annually',
        mensalmente: 'Monthly',
        semanalmente: 'Weekly',
        diariamente: 'Daily',
        porHora: 'Hourly',
        constantemente: 'Constantly',
      },
      po: {
        quaseImpossivel: 'Almost impossible',
        muitoImprovavel: 'Highly unlikely',
        improvavel: 'Unlikely',
        possivel: 'Possible',
        plausivel: 'Plausible',
        provavel: 'Likely',
        muitoProvavel: 'Very likely',
        quaseCerteza: 'Almost certain',
      },
      np: {
        de1a2: '1 to 2 people',
        de3a7: '3 to 7 people',
        de8a15: '8 to 15 people',
        de16a50: '16 to 50 people',
        maisDe50: 'More than 50 people',
      },
    },
    srpcs: {
      severidade: {
        S1: 'Minor injury (normally reversible)',
        S2: 'Serious injury (normally irreversible) including death',
      },
      frequencia: {
        F1: 'Rare to relatively frequent and/or short exposure time',
        F2: 'Frequent to continuous and/or long exposure time',
      },
      possibilidade: {
        P1: 'Possible under specific conditions',
        P2: 'Hardly ever possible',
      },
      categoria: {
        B: 'Category B - Basic safety requirements',
        '1': 'Category 1 - Basic safety requirements and well-tried components',
        '2': 'Category 2 - Periodic checking of the safety function',
        '3': 'Category 3 - Redundancy and monitoring required',
        '4': 'Category 4 - Redundancy and continuous monitoring required',
      },
      plr: {
        a: 'PLr a - Low',
        b: 'PLr b - Low to medium',
        c: 'PLr c - Medium',
        d: 'PLr d - Medium to high',
        e: 'PLr e - High',
      },
      sil: {
        '1': 'SIL 1 - Low',
        '2': 'SIL 2 - Medium',
        '3': 'SIL 3 - High',
        '4': 'SIL 4 - Very high',
      },
      observacao: {
        avaliarCasoEspecifico: 'PL c (or d) - assess the specific case',
      },
    },
    partesCorpo: {
      corpoInteiro: 'Whole body',
      cabeca: 'Head',
      pescoco: 'Neck',
      torax: 'Chest',
      membrosSuperiores: 'Upper limbs',
      membrosInferiores: 'Lower limbs',
      maos: 'Hands',
      pes: 'Feet',
      olhos: 'Eyes',
      face: 'Face',
    },
    perigos: {
      texto: {
      aceleracaoDesaceleracao: 'acceleration, deceleration',
      cantosVivos: 'sharp corners',
      aproximacaoDeUmElementoMovelAUmaParteFixa: 'approach of a moving element to a fixed part',
      corteDePecas: 'cutting parts',
      elementosElasticos: 'elastic elements',
      quedaDeObjetos: 'falling objects',
      gravidade: 'gravity',
      alturaAPartirDoSolo: 'height from the ground',
      altaPressao: 'high pressure',
      instabilidade: 'instability',
      energiaCinetica: 'kinetic energy',
      mobilidadeDaMaquina: 'machine mobility',
      elementosMoveis: 'moving elements',
      elementosRotativos: 'rotating elements',
      superficieAsperaEscorregadia: 'rough, slippery surface',
      arestasCortantes: 'sharp edges',
      energiaArmazenada: 'stored energy',
      vacuo: 'vacuum',
      atropelamentos: 'run-over',
      arremessos: 'ejection',
      esmagamento: 'crushing',
      corteEMutilacao: 'cutting and severing',
      segurarEPrender: 'drawing-in and trapping',
      enroscar: 'entanglement',
      friccaoEAbrasao: 'friction and abrasion',
      impacto: 'impact',
      injecao: 'injection',
      raspagem: 'scraping',
      escorregamentoTropecoEQueda: 'slipping, tripping and falling',
      perfuracao: 'puncture',
      sufocamento: 'suffocation',
      arcos: 'arcs',
      fenomenosEletromagneticos: 'electromagnetic phenomena',
      partesVivas: 'live parts',
      baixaRigidezDieletrica: 'low dielectric strength',
      partesVivasSobCondicoesDeFalha: 'live parts under fault conditions',
      curtoCircuito: 'short circuit',
      radiacaoTermica: 'thermal radiation',
      queimadura: 'burn',
      efeitosQuimicos: 'chemical effects',
      efeitosEmImplantesMedicos: 'effects on medical implants',
      eletrocussao: 'electrocution',
      quedaOuArremesso: 'falling or being thrown',
      incendio: 'fire',
      projecaoDeFagulhas: 'sparks being thrown',
      choque: 'shock',
      explosao: 'explosion',
      objetosOuMateriaisComAltaOuBaixaTemperatura: 'objects or materials at high or low temperature',
      radiacaoProvenienteDeFontesQuentes: 'radiation from heat sources',
      desidratacao: 'dehydration',
      desconforto: 'discomfort',
      congelamento: 'freezing',
      danosCausadosPelaRadiacaoDeFontesQuentes: 'damage caused by radiation from heat sources',
      escaldo: 'scalding',
      fenomenoDeCavitacao: 'cavitation',
      sistemaDeExaustao: 'exhaust system',
      vazamentoDeGasEmAltaVelocidade: 'high-velocity gas leak',
      processosDeProducaoEstampagemCorteEtc: 'production processes (stamping, cutting, etc.)',
      partesMoveis: 'moving parts',
      superficiesEmAtrito: 'rubbing surfaces',
      pecasRotativasDesbalanceadas: 'unbalanced rotating parts',
      ruidosPneumaticos: 'pneumatic noise',
      pecasDesgastadas: 'worn parts',
      perdaDeConsciencia: 'loss of consciousness',
      perdaDeEquilibrio: 'loss of balance',
      perdaPermanenteDeAudicao: 'permanent hearing loss',
      estresse: 'stress',
      zumbido: 'tinnitus',
      cansaco: 'tiredness',
      outrosComoPorExemploMecanicosEletricosEmDecorrenciaDaInterferenciaEmComunicacoesOuSinaisAcusticos: 'others (for example mechanical, electrical) resulting from interference with speech communication or acoustic signals',
      desalinhamentoDePartesMoveis: 'misalignment of moving parts',
      equipamentosMoveis: 'mobile equipment',
      equipamentosQueVibram: 'vibrating equipment',
      morbidadeLombar: 'low-back morbidity',
      disfuncoesNeurologicas: 'neurological disorders',
      disfuncoesOsteoarticulares: 'osteo-articular disorders',
      traumasNaColuna: 'spinal trauma',
      disfuncoesVasculares: 'vascular disorders',
      fontesDeRadiacaoIonizante: 'ionizing radiation sources',
      radiacoesEletromagneticasDeBaixaFrequencia: 'low-frequency electromagnetic radiation',
      radiacaoOticaInfravermelhoVisivelOuUltravioletaIncluindoLaser: 'optical radiation (infrared, visible or ultraviolet), including laser',
      radiacoesEletromagneticasEmRadiofrequencia: 'radio-frequency electromagnetic radiation',
      danosNosOlhosEPele: 'damage to eyes and skin',
      efeitosNaCapacidadeReprodutiva: 'effects on reproductive capability',
      mutacoes: 'mutations',
      dorDeCabecaInsoniaEtc: 'headache, insomnia, etc',
      efeitosCancerigenos: 'carcinogenic effects',
      aerossois: 'aerosols',
      agentesBiologicosEMicrobiologicosViraisOuBacterianos: 'biological and microbiological agents (viral or bacterial)',
      combustiveis: 'fuels',
      poeira: 'dust',
      explosivos: 'explosives',
      fibras: 'fibres',
      inflamaveis: 'flammables',
      fluidos: 'fluids',
      fumos: 'fumes',
      gases: 'gases',
      misturas: 'mixtures',
      oxidantes: 'oxidizers',
      dificuldadesParaRespiracaoAsfixia: 'breathing difficulties, asphyxia',
      cancer: 'cancer',
      corrosao: 'corrosion',
      explosoes: 'explosions',
      fogo: 'fire',
      infeccoes: 'infections',
      envenenamento: 'poisoning',
      sensibilizacao: 'sensitization',
      acesso: 'access',
      projetoOuLocalizacaoDeIndicadoresIndicadoresEPaineis: 'design or location of indicators, indicators and display units',
      projetoLocalizacaoOuIdentificacaoDeDispositivosDeComando: 'design, location or identification of control devices',
      esforcos: 'effort',
      cintilacaoBrilhoSombraEfeitoEstroboscopio: 'flicker, glare, shadow, stroboscopic effect',
      luzLocal: 'local lighting',
      sobrecargaMentalOciosidade: 'mental overload, underload',
      postura: 'posture',
      atividadeRepetitiva: 'repetitive activity',
      visibilidade: 'visibility',
      fadiga: 'fatigue',
      disturbiosMusculoEsqueleticos: 'musculoskeletal disorders',
      outrosComoPorExemplo: 'others (for example...)',
      poeiraOuNeblina: 'dust or fog',
      pertubacaoEletromagnetica: 'electromagnetic disturbance',
      descargasAtmosfericas: 'lightning',
      umidade: 'humidity',
      poluicao: 'pollution',
      neve: 'snow',
      temperatura: 'temperature',
      agua: 'water',
      vento: 'wind',
      faltaDeOxigenio: 'lack of oxygen',
      doencasBrandas: 'mild illnesses',
      quedaOuEscorregamento: 'falling or slipping',
      asfixia: 'asphyxia',
      qualquerOutraConsequenciaDoEfeitoCausadoPorFontesDePerigosDaMaquinaOuPartesDaMesma: 'any other consequence of the effect caused by hazard sources on the machine or its parts',
      porExemploAtividadesRepetitivasEsforcosAmbientesEmAltaTemperatura: 'for example, repetitive activities + effort + high-temperature environments',
      porExemploDesidratacaoPerdaDeConscienciaEAtaqueCardiaco: 'for example, dehydration, loss of consciousness and heart attack',
      },
    },
  },
};
